/* =========================================================================
   Shapes — the five candidate layout geometries, plus the metrics shown
   alongside each one.

   Every layout has the same signature and the same convention as the shipped
   engine (`DevelopmentPlan/helpers.ts`): it returns CENTRED coordinates with
   the core at {0,0}; the canvas adds the world offset at render time. That
   means a candidate adopted from here can be lifted into `helpers.ts` as-is.

   Parameters are the exact ones quoted in the sizing analysis, so what you see
   here is what those numbers describe.
   ========================================================================= */

import { CARD_H, CARD_W, CORE_ID, ShapeNode, ShapeTrack } from './mock';

export interface XY {
  x: number;
  y: number;
}

export type EdgeStyle = 'ortho' | 'straight' | 'octi';

export type LayoutFn = (
  nodes: ShapeNode[],
  tracks: ShapeTrack[],
) => Record<string, XY>;

export interface Candidate {
  key: string;
  label: string;
  /** One-line description of the geometry. */
  blurb: string;
  /** How edges should be drawn for this geometry. */
  edgeStyle: EdgeStyle;
  /** The parameters, for display. */
  params: string;
  layout: LayoutFn;
  /** A not-yet-built candidate: the page shows an empty-state panel for it
   *  instead of rendering the canvas. `layout` is never invoked while true. */
  placeholder?: boolean;
}

/* ---- Helpers ------------------------------------------------------------ */

function layersOf(
  nodes: ShapeNode[],
  trackCode: string,
): Record<number, ShapeNode[]> {
  const layers: Record<number, ShapeNode[]> = {};
  nodes
    .filter(n => n.track_code === trackCode)
    .forEach(n => {
      (layers[n.depth] = layers[n.depth] || []).push(n);
    });
  Object.values(layers).forEach(arr =>
    arr.sort((a, b) => a.code.localeCompare(b.code)),
  );
  return layers;
}

/* ---- A / A2. Radial fan, siblings spread ANGULARLY in the track's sector -- */

function radialAngular(r0: number, layerStep: number): LayoutFn {
  return (nodes, tracks) => {
    const pos: Record<string, XY> = { [CORE_ID]: { x: 0, y: 0 } };
    const sector = (2 * Math.PI) / tracks.length;
    tracks.forEach((track, t) => {
      const base = t * sector - Math.PI / 2; // start at 12 o'clock
      const layers = layersOf(nodes, track.code);
      Object.keys(layers).forEach(key => {
        const depth = Number(key);
        const arr = layers[depth];
        const m = arr.length;
        const r = r0 + depth * layerStep;
        arr.forEach((node, k) => {
          // spread across the sector leaving a guard band at each edge
          const frac = m === 1 ? 0 : (k / (m - 1) - 0.5) * 0.7;
          const a = base + frac * sector;
          pos[node.code] = { x: r * Math.cos(a), y: r * Math.sin(a) };
        });
      });
    });
    return pos;
  };
}

/* ---- B / B2. Radial rail, siblings spread LINEARLY across the ray -------- */

function radialRail(r0: number, layerStep: number, slotGap: number): LayoutFn {
  return (nodes, tracks) => {
    const pos: Record<string, XY> = { [CORE_ID]: { x: 0, y: 0 } };
    const sector = (2 * Math.PI) / tracks.length;
    tracks.forEach((track, t) => {
      const a = t * sector - Math.PI / 2;
      const ux = Math.cos(a);
      const uy = Math.sin(a); // along the ray
      const px = -uy;
      const py = ux; // perpendicular to it
      const layers = layersOf(nodes, track.code);
      Object.keys(layers).forEach(key => {
        const depth = Number(key);
        const arr = layers[depth];
        const m = arr.length;
        const r = r0 + depth * layerStep;
        arr.forEach((node, k) => {
          const off = (k - (m - 1) / 2) * slotGap;
          pos[node.code] = {
            x: r * ux + off * px,
            y: r * uy + off * py,
          };
        });
      });
    });
    return pos;
  };
}

/* ---- C. Vertical swimlanes ---------------------------------------------- */

function lanes(colGap: number, layerStep: number, slotGap: number): LayoutFn {
  return (nodes, tracks) => {
    const pos: Record<string, XY> = {};
    // Column width is driven by the widest layer in the WHOLE plan, so every
    // lane is the same width and the grid reads as a grid.
    let widest = 1;
    tracks.forEach(track => {
      Object.values(layersOf(nodes, track.code)).forEach(arr => {
        widest = Math.max(widest, arr.length);
      });
    });
    const colW = widest * slotGap + colGap;

    tracks.forEach((track, t) => {
      const cx = (t - (tracks.length - 1) / 2) * colW;
      const layers = layersOf(nodes, track.code);
      Object.keys(layers).forEach(key => {
        const depth = Number(key);
        const arr = layers[depth];
        const m = arr.length;
        arr.forEach((node, k) => {
          pos[node.code] = {
            x: cx + (k - (m - 1) / 2) * slotGap,
            y: depth * layerStep,
          };
        });
      });
    });

    // The core sits one layer step ABOVE the first row, centred — the hub the
    // lanes hang from, rather than the point they radiate from.
    pos[CORE_ID] = { x: 0, y: -layerStep };
    return pos;
  };
}

/* ---- D. Octilinear "subway" --------------------------------------------

   Subway-map abstraction: tracks leave the hub in one of 8 fixed directions
   (N/S/E/W + the four 45° diagonals) and run as straight rails; edges are
   drawn with 45°/90° segments only (see edgePaths.octiPath).

   PROVABLE non-overlap. Every node is placed on a distinct cell of an integer
   grid and rendered at (col*PITCH, row*PITCH). Two cards overlap only if
   |Δx| < CARD_W AND |Δy| < CARD_H. With PITCH >= CARD_W:
     · different column  -> |Δx| >= PITCH >= CARD_W  -> not overlapping;
     · same column       -> different row -> |Δy| >= PITCH >= CARD_W > CARD_H
                            -> not overlapping.
   So DISTINCT CELLS + PITCH >= CARD_W  ==>  no two cards can overlap, by
   construction. The allocator below only ever writes a node into a cell not in
   `occupied`, so the "distinct cells" premise holds for every node — the proof
   is maintained as an invariant, not checked after the fact. (A square pitch,
   PITCHx == PITCHy, is also what makes the 45° rails render at a true 45°.)   */

// 8 grid directions, clockwise from up; row increases downward.
const DIRS8: Array<[number, number]> = [
  [0, -1], // N
  [1, -1], // NE
  [1, 0], // E
  [1, 1], // SE
  [0, 1], // S
  [-1, 1], // SW
  [-1, 0], // W
  [-1, -1], // NW
];

interface OctiParams {
  pitch: number; // px per grid cell (>= CARD_W, and square, keeps the proof + true 45°)
  coreGap: number; // cells from the hub to a track's first layer
  layerStep: number; // cells added per prerequisite-depth layer
  siblingGap: number; // cells between siblings within a layer (perpendicular to the rail)
  railGap: number; // cells between parallel rails that share a direction
}

function octilinear(p: OctiParams): LayoutFn {
  return (nodes, tracks) => {
    const occupied = new Set<string>();
    const key = (c: number, r: number) => `${c},${r}`;

    // Claim the first FREE cell at/near (idealC, idealR) by a deterministic
    // ring search. Only unoccupied cells are ever claimed, so every returned
    // cell is unique — this is what upholds the non-overlap invariant.
    const claim = (idealC: number, idealR: number): XY => {
      const c0 = Math.round(idealC);
      const r0 = Math.round(idealR);
      if (!occupied.has(key(c0, r0))) {
        occupied.add(key(c0, r0));
        return { x: c0 * p.pitch, y: r0 * p.pitch };
      }
      for (let radius = 1; radius < 256; radius += 1) {
        for (let dc = -radius; dc <= radius; dc += 1) {
          for (let dr = -radius; dr <= radius; dr += 1) {
            if (Math.max(Math.abs(dc), Math.abs(dr)) !== radius) continue; // ring edge only
            const c = c0 + dc;
            const r = r0 + dr;
            if (!occupied.has(key(c, r))) {
              occupied.add(key(c, r));
              return { x: c * p.pitch, y: r * p.pitch };
            }
          }
        }
      }
      occupied.add(key(c0, r0)); // unreachable on any real plan
      return { x: c0 * p.pitch, y: r0 * p.pitch };
    };

    const pos: Record<string, XY> = {};
    occupied.add(key(0, 0));
    pos[CORE_ID] = { x: 0, y: 0 };

    const dirUse: Record<number, number> = {};
    tracks.forEach((track, t) => {
      // Spread tracks around the compass; reused directions become parallel rails.
      const di = Math.round((t * 8) / tracks.length) % 8;
      const use = dirUse[di] || 0;
      dirUse[di] = use + 1;
      // parallel offset: 0, +1, -1, +2, -2, ...
      const par =
        use === 0
          ? 0
          : use % 2 === 1
            ? Math.ceil(use / 2)
            : -Math.ceil(use / 2);

      const [dc, dr] = DIRS8[di];
      const pc = -dr; // perpendicular (90° cw) — still one of the 8 grid dirs
      const pr = dc;

      const layers = layersOf(nodes, track.code);
      Object.keys(layers)
        .map(Number)
        .sort((x, y) => x - y)
        .forEach(depth => {
          const arr = layers[depth];
          const m = arr.length;
          const along = p.coreGap + depth * p.layerStep;
          arr.forEach((node, k) => {
            const perp = par * p.railGap + (k - (m - 1) / 2) * p.siblingGap;
            pos[node.code] = claim(
              dc * along + pc * perp,
              dr * along + pr * perp,
            );
          });
        });
    });

    return pos;
  };
}

/* ---- The candidates ----------------------------------------------------- */

export const CANDIDATES: Candidate[] = [
  {
    key: 'radial-angular-360',
    label: 'A · Radial fan',
    blurb:
      'Each track owns a 1/N sector; siblings spread by angle inside it. The "just fan them out" fix.',
    params: 'r₀ = 360, layer step = 240',
    edgeStyle: 'straight',
    layout: radialAngular(360, 240),
  },
  {
    key: 'radial-angular-1900',
    label: 'A2 · Radial fan, pushed out',
    blurb:
      'Same geometry as A, with the first ring pushed out far enough that sectors stop colliding.',
    params: 'r₀ = 1900, layer step = 300',
    edgeStyle: 'straight',
    layout: radialAngular(1900, 300),
  },
  {
    key: 'radial-rail-800',
    label: 'B · Radial rail',
    blurb:
      'Each track is a straight rail along its ray; siblings offset perpendicular by a fixed gap.',
    params: 'r₀ = 800, layer step = 260, slot gap = 210',
    edgeStyle: 'straight',
    layout: radialRail(800, 260, 210),
  },
  {
    key: 'radial-rail-1200',
    label: 'B2 · Radial rail, pushed out',
    blurb: 'Same rail geometry as B, starting further from the core.',
    params: 'r₀ = 1200, layer step = 280, slot gap = 210',
    edgeStyle: 'straight',
    layout: radialRail(1200, 280, 210),
  },
  {
    key: 'lanes',
    label: 'C · Vertical swimlanes',
    blurb:
      'Each track is a column; depth runs downward. Reuses the shipped orthogonal edge router unchanged.',
    params: 'column gap = 120, layer step = 165, slot gap = 200',
    edgeStyle: 'ortho',
    layout: lanes(120, 165, 200),
  },
  {
    key: 'subway',
    label: 'D · Subway (octilinear)',
    blurb:
      'Subway-map abstraction: tracks radiate on 8 fixed headings; every edge runs at 45° or 90° only. Nodes are grid-snapped so overlaps are impossible by construction.',
    params:
      'pitch = 210, core gap = 3, layer step = 2, sibling gap = 2, rail gap = 4',
    edgeStyle: 'octi',
    layout: octilinear({
      pitch: 205,
      coreGap: 2,
      layerStep: 2,
      siblingGap: 3,
      railGap: 6,
    }),
  },
];

/* ---- Metrics ------------------------------------------------------------ */

export interface LayoutMetrics {
  nodes: number;
  overlaps: number;
  /** Tightest overlapping pair, for diagnosis. */
  tightest: { dx: number; dy: number } | null;
  /** Content bounds in layout space, card extents included. */
  minX: number;
  minY: number;
  /** Size of the padded world the canvas actually renders. */
  worldW: number;
  worldH: number;
  /** Zoom needed to fit that world in the given viewport. */
  fitZoom: number;
}

/** Half-extent of the core emblem (84px circle), so it can't fall outside. */
const CORE_HALF = 42;

/**
 * Overlap count + the true (non-symmetric) content bounds.
 *
 * Bounds are measured from the real min/max rather than assumed symmetric about
 * the origin: the swimlane candidate lays every node BELOW the origin, so a
 * symmetric world would double its height and make its fit-zoom look twice as
 * bad as it is. Every candidate is measured the same way, so the numbers on
 * each subtab are comparable.
 */
export function measure(
  positions: Record<string, XY>,
  viewportW: number,
  viewportH: number,
  margin: number,
): LayoutMetrics {
  const pts = Object.entries(positions)
    .filter(([code]) => code !== CORE_ID)
    .map(([, p]) => p);

  let overlaps = 0;
  let tightest: { dx: number; dy: number } | null = null;
  for (let i = 0; i < pts.length; i += 1) {
    for (let j = i + 1; j < pts.length; j += 1) {
      const dx = Math.abs(pts[i].x - pts[j].x);
      const dy = Math.abs(pts[i].y - pts[j].y);
      if (dx < CARD_W && dy < CARD_H) {
        overlaps += 1;
        if (!tightest || dx + dy < tightest.dx + tightest.dy) {
          tightest = { dx: Math.round(dx), dy: Math.round(dy) };
        }
      }
    }
  }

  // Card corners bound the nodes; the core is bounded by its own radius.
  const core = positions[CORE_ID];
  const xsLo = pts.map(p => p.x - CARD_W / 2);
  const xsHi = pts.map(p => p.x + CARD_W / 2);
  const ysLo = pts.map(p => p.y - CARD_H / 2);
  const ysHi = pts.map(p => p.y + CARD_H / 2);
  if (core) {
    xsLo.push(core.x - CORE_HALF);
    xsHi.push(core.x + CORE_HALF);
    ysLo.push(core.y - CORE_HALF);
    ysHi.push(core.y + CORE_HALF);
  }

  const minX = Math.min(...xsLo) - margin;
  const maxX = Math.max(...xsHi) + margin;
  const minY = Math.min(...ysLo) - margin;
  const maxY = Math.max(...ysHi) + margin;

  const worldW = Math.round(maxX - minX);
  const worldH = Math.round(maxY - minY);

  return {
    nodes: pts.length,
    overlaps,
    tightest,
    minX,
    minY,
    worldW,
    worldH,
    fitZoom: Math.min(viewportW / worldW, viewportH / worldH),
  };
}
